import React, { useCallback, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSanitize from 'rehype-sanitize';
import { fetchImageReport } from '../services/imageReportService';
import { ImageReportDetail } from '../types';
import apiClient from '../services/apiClient';
import './ImageReportDetailPage.css';

const ImageReportDetailPage: React.FC = () => {
  const params = useParams<{ reportId: string }>();
  const reportId = params.reportId ? decodeURIComponent(params.reportId) : undefined;
  const navigate = useNavigate();
  const [report, setReport] = useState<ImageReportDetail | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null);
  const [imageLoading, setImageLoading] = useState<boolean>(false);
  const [imageFetchError, setImageFetchError] = useState<string | null>(null);

  useEffect(() => {
    if (!reportId) {
      setError('未提供报告编号。');
      setLoading(false);
      return;
    }

    let cancelled = false;
    const loadReport = async () => {
      setLoading(true);
      setError(null);
      try {
        const detail = await fetchImageReport(reportId);
        if (!cancelled) {
          setReport(detail);
        }
      } catch (err) {
        console.error('加载影像报告失败', err);
        if (!cancelled) {
          setError('加载影像报告失败，请稍后再试。');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void loadReport();
    return () => {
      cancelled = true;
    };
  }, [reportId]);

  const handleBack = () => {
    navigate(-1);
  };

  const resolveImageRequestPath = useCallback((imageUrl: string): string => {
    if (/^https?:\/\//i.test(imageUrl)) {
      return imageUrl;
    }
    let normalized = imageUrl.trim();
    if (normalized.startsWith('/')) {
      normalized = normalized.slice(1);
    }
    if (normalized.startsWith('api/')) {
      normalized = normalized.slice(4);
    }
    return normalized;
  }, []);

  useEffect(() => {
    let cancelled = false;
    let objectUrl: string | null = null;

    const fetchImage = async () => {
      if (!report?.image_url) {
        setImagePreviewUrl(null);
        setImageFetchError(null);
        return;
      }

      setImageLoading(true);
      setImageFetchError(null);
      try {
        const requestPath = resolveImageRequestPath(report.image_url);
        const response = await apiClient.get<Blob>(requestPath, { responseType: 'blob' });
        if (cancelled) {
          return;
        }
        const url = URL.createObjectURL(response.data);
        objectUrl = url;
        setImagePreviewUrl(url);
      } catch (err) {
        console.error('加载影像文件失败', err);
        if (!cancelled) {
          setImagePreviewUrl(null);
          setImageFetchError('影像预览加载失败');
        }
      } finally {
        if (!cancelled) {
          setImageLoading(false);
        }
      }
    };

    void fetchImage();

    return () => {
      cancelled = true;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [report?.image_url, resolveImageRequestPath]);

  const handleOpenRawImage = useCallback(async () => {
    if (!report?.image_url) {
      return;
    }

    if (imagePreviewUrl) {
      window.open(imagePreviewUrl, '_blank', 'noopener,noreferrer');
      return;
    }

    try {
      const requestPath = resolveImageRequestPath(report.image_url);
      const response = await apiClient.get<Blob>(requestPath, { responseType: 'blob' });
      const objectUrl = URL.createObjectURL(response.data);
      window.open(objectUrl, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
    } catch (err) {
      console.error('打开原始影像失败', err);
      setImageFetchError('原始影像打开失败，请稍后重试。');
    }
  }, [imagePreviewUrl, report, resolveImageRequestPath]);

  const renderContent = () => {
    if (loading) {
      return <div className="report-status subtle">正在加载影像报告…</div>;
    }

    if (error) {
      return <div className="report-status error">{error}</div>;
    }

    if (!report) {
      return <div className="report-status subtle">暂未查询到该影像报告。</div>;
    }

    return (
      <>
        <section className="report-summary-card">
          <div className="report-summary-header">
            <span className="report-type-tag">{report.focus_label}</span>
            {report.created_at && (
              <span className="report-timestamp">生成于 {new Date(report.created_at).toLocaleString()}</span>
            )}
          </div>
          <div className="report-meta-grid">
            <div>
              <span className="meta-label">报告编号</span>
              <span className="meta-value">{report.report_id}</span>
            </div>
            {report.conversation_id && (
              <div>
                <span className="meta-label">关联会话</span>
                <span className="meta-value">#{report.conversation_id}</span>
              </div>
            )}
          </div>
        </section>

        {report?.image_url && (
          <section className="report-image-card">
            <div className="report-image-preview">
              {imageLoading && <div className="report-image-message">影像加载中…</div>}
              {!imageLoading && imagePreviewUrl && (
                <img src={imagePreviewUrl} alt="影像采集预览" />
              )}
              {!imageLoading && !imagePreviewUrl && imageFetchError && (
                <span className="report-image-message error">{imageFetchError}</span>
              )}
            </div>
            <button type="button" className="report-image-button" onClick={handleOpenRawImage}>
              查看原始影像
            </button>
            {imageFetchError && (
              <p className="report-image-error">{imageFetchError}</p>
            )}
          </section>
        )}

        <section className="report-analysis-card">
          <h3>诊断解析</h3>
          <div className="report-markdown">
            <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeSanitize]}>
              {report.analysis}
            </ReactMarkdown>
          </div>
        </section>
      </>
    );
  };

  return (
    <motion.div
      className="unified-page-wrapper image-report-page"
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45 }}
    >
      <div className="unified-page-header image-report-header">
        <h2>影像诊断报告</h2>
        <button type="button" className="report-back-button" onClick={handleBack}>
          返回上一页
        </button>
      </div>
      <div className="unified-page-content-scrollable image-report-content">
        {renderContent()}
      </div>
    </motion.div>
  );
};

export default ImageReportDetailPage;
